import { type OutboundEmailFieldsFragment, OutboundEmailStatus } from '~/api/graphql';
import { rowDateTimeFormatter } from '~/components/table-formatters';
import { formatSendTime, formatTimeOmittingSameDay } from '~/lib/scheduled-send';
import { type OutboxRow, outboxRowStatus } from './outbox-rows';

/** Past this, a scheduled email went out late rather than on time. */
const SCHEDULED_SEND_LATE_AFTER_MS = 5 * 60_000;

/**
 * What a row says about the time an agent picked: when it sends, or how late
 * it went. Null for an email that was never scheduled.
 */
export const outboxRowSendTimeText = (row: OutboxRow, opts: { nowMs: number }): string | null => {
  const outbound = row.outbound;
  if (!outbound?.scheduledFor) {
    return null;
  }
  const scheduledFor = new Date(outbound.scheduledFor);
  switch (outboxRowStatus(row, opts)) {
    case 'scheduled':
      return `Sends ${formatSendTime(scheduledFor)}`;
    case 'sent': {
      const sentAt = outbound.sentAt ? new Date(outbound.sentAt) : null;
      const isSentLate =
        !!sentAt && sentAt.getTime() - scheduledFor.getTime() > SCHEDULED_SEND_LATE_AFTER_MS;
      return sentAt && isSentLate
        ? `Sent ${formatTimeOmittingSameDay(sentAt, scheduledFor)} (scheduled ${formatTimeOmittingSameDay(scheduledFor, sentAt)})`
        : null;
    }
    default:
      return null;
  }
};

/**
 * The time column of a SCHEDULED row, dated by the schedule's latest change:
 * scheduling it or a reschedule.
 */
export const scheduledRowTimestamp = (
  outbound: Pick<OutboundEmailFieldsFragment, 'updatedAt'>
): { label: string; title: string; unconfirmed: boolean } => ({
  label: rowDateTimeFormatter.format(new Date(outbound.updatedAt)),
  title: "When the email's schedule last changed",
  unconfirmed: false
});

/** The line under the status badge of a scheduled send that failed. */
export const scheduledSendStatusNote = (
  outbound: Pick<OutboundEmailFieldsFragment, 'status' | 'scheduledFor'> | undefined
): string | null =>
  outbound?.scheduledFor && outbound.status === OutboundEmailStatus.Failed ? 'Was scheduled' : null;

export const OUTBOX_ROW_ACTION_CLASS =
  'relative z-10 shrink-0 rounded-sm text-xs font-medium whitespace-nowrap underline focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:no-underline disabled:text-neutral-400 dark:disabled:text-neutral-500';
